import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoneyWithCurrency } from '../../../utils/formatter';
import { FindDocumentById } from 'src/graphql/documents';
import { FindTransactionById } from 'src/graphql/conciliacion';
import CustomTableItem from '../../shared/customComponents/customTableItem';
import DocumentContent from '../modalDetalleDocumento/components/DocumentContent';
import TransactionContent from '../modalDetalleDocumento/components/TransactionContent';

const MatchesTab = ({ data }) => {
  const { t } = useTranslation('documents');

  const processMatches = (matches) => {
    if (!matches) return { documentInput: [], transactionInput: [], settlesIds: [] };

    const result = {
      documentInput: [],
      transactionInput: [],
      settlesIds: [],
    };

    const inList = Array.isArray(matches.inMatch) ? matches.inMatch : [];
    const outList = Array.isArray(matches.outMatch) ? matches.outMatch : [];

    inList.forEach((match) => {
      if (match.type === "TRANSACTION") {
        result.transactionInput.push(match.id);
      } else if (["TAX_DOCUMENT", "OBLIGATION", "CREDIT"].includes(match.type)) {
        result.documentInput.push(match.id);
      } else if (["MANUAL"].includes(match.type)) {
        result.settlesIds.push(match.settleId);
      }
    });

    outList.forEach((match) => {
      if (match.type === "TRANSACTION") {
        result.transactionInput.push(match.id);
      } else if (["TAX_DOCUMENT", "OBLIGATION", "CREDIT"].includes(match.type)) {
        result.documentInput.push(match.id);
      }
    });

    return result;
  };

  const processedData = processMatches(data?.meta?.matchs || []);
  const { data: documentData } = FindDocumentById({ ids: processedData.documentInput });
  const { data: transactionData } = FindTransactionById({ ids: processedData.transactionInput });

  const classifyMatches = (matches, documents, transactions) => {
    const inRows = Array.isArray(matches?.inMatch) ? matches.inMatch : [];
    const outRows = Array.isArray(matches?.outMatch) ? matches.outMatch : [];
    const inMatchIds = inRows.map((item) => item.id);
    const settleIds = inRows.map((item) => item.settleId).filter((id) => id != null);
    const outMatchIds = outRows.map((item) => item.id);

    const documentInMatches = documents?.filter(doc =>
      inMatchIds.includes(doc._id || doc.id)
    ) || [];

    const settleInMatches = inRows.filter((match) => settleIds.includes(match.settleId));

    const documentOutMatches = documents?.filter(doc =>
      outMatchIds.includes(doc._id || doc.id)
    ) || [];

    const transactionInMatches = transactions?.filter(trans =>
      inMatchIds.includes(trans.id)
    ) || [];

    const transactionOutMatches = transactions?.filter(trans =>
      outMatchIds.includes(trans.id)
    ) || [];

    return {
      documentInMatches,
      transactionInMatches,
      documentOutMatches,
      transactionOutMatches,
      settleInMatches
    };
  };

  const {
    documentInMatches,
    transactionInMatches,
    documentOutMatches,
    transactionOutMatches,
    settleInMatches
  } = classifyMatches(
    data?.meta?.matchs || [],
    documentData?.findDocumentsById || [],
    transactionData?.findTransactionsById || []
  );

  const calculateTotals = () => {
    let totalInMatch = 0;
    let totalOutMatch = 0;

    documentInMatches.forEach(doc => {
      const matches = doc.meta?.matchs;
      if (!matches) return;

      if (data?.documentType === 'TAX_DOCUMENT' && doc.documentType === 'OBLIGATION') {
        if (Array.isArray(matches.outMatch)) {
          matches.outMatch.forEach((match) => {
            if (match.id === data.id) {
              totalInMatch += match.matchedAmount || match.amount;
            }
          });
        }
      } else if (data?.documentType === 'OBLIGATION' && doc.documentType === 'OBLIGATION') {
        matches.outMatch?.forEach((match) => {
          if (match.id === data.id) {
            totalInMatch += match.matchedAmount || match.localValue || match.amount;
          }
        });
      } else if (Array.isArray(matches.inMatch)) {
        matches.inMatch.forEach((match) => {
          if (match.id === data.id) {
            totalInMatch += match.matchedAmount || match.amount;
          }
        });
      }
    });

    transactionInMatches.forEach(trans => {
      const matches = trans?.matchs;
      if (!Array.isArray(matches)) return;
      matches.forEach(match => {
        if (match.docId === data.id) {
          totalInMatch += match.matchedAmount || match.amount;
        }
      });
    });

    if (settleInMatches.length > 0) {
      settleInMatches.forEach(settle => {
        totalInMatch += settle?.amount;
      });
    }

    documentOutMatches.forEach(doc => {
      const matches = doc.meta?.matchs;
      if (!matches) return;

      if (data?.documentType === 'OBLIGATION' && doc.documentType === 'TAX_DOCUMENT') {
        if (Array.isArray(matches.inMatch)) {
          matches.inMatch.forEach((match) => {
            if (match.id === data.id) {
              totalOutMatch += match.matchedAmount || match.amount;
            }
          });
        }
      } else if (data?.documentType === 'OBLIGATION' && doc.documentType === 'OBLIGATION') {
        matches.inMatch?.forEach((match) => {
          if (match.id === data.id) {
            totalOutMatch += match.matchedAmount || match.localValue || match.amount;
          }
        });
      } else if (Array.isArray(matches.outMatch)) {
        matches.outMatch.forEach((match) => {
          if (match.id === data.id) {
            totalOutMatch += match.matchedAmount || match.amount;
          }
        });
      }
    });

    transactionOutMatches.forEach(trans => {
      const matches = trans?.matchs;
      if (!Array.isArray(matches)) return;
      matches.forEach(match => {
        if (match.docId === data.id) {
          totalOutMatch += match.matchedAmount || match.amount;
        }
      });
    });

    return { totalInMatch, totalOutMatch };
  };

  const { totalInMatch, totalOutMatch } = calculateTotals();

  const columnHeaders = (
    <div className="tw-flex tw-px-5">
      <div className="tw-w-1/6">
        <p className="tw-text-[12px] tw-font-light tw-text-gray-700 !tw-p-0 !tw-m-0">{t('detail.matchesSection.amountColumn')}</p>
      </div>
      <div className="tw-w-1/6">
        <p className="tw-text-[12px] tw-font-light tw-text-gray-700 !tw-p-0 !tw-m-0">{t('detail.matchesSection.dateColumn')}</p>
      </div>
      <div className="tw-w-4/6">
        <p className="tw-text-[12px] tw-font-light tw-text-gray-700 !tw-p-0 tw-ms-32">{t('detail.matchesSection.descriptionColumn')}</p>
      </div>
    </div>
  );

  return (
    <div className="document-content">
      <div className="tw-flex tw-flex-col tw-gap-1">
        <h4 className="!tw-m-0 tw-text-[15px] tw-font-semibold tw-text-gray-700">{t('detail.matchesSection.title')}</h4>
        <h5 className="!tw-m-0 tw-text-[13px] tw-font-normal tw-text-gray-500">{t('detail.matchesSection.subtitle')}</h5>
      </div>

      <div className="tw-border-solid tw-border-[1px] tw-border-gray-200 tw-rounded-[3px] tw-mt-4 tw-flex tw-flex-col tw-gap-4">
        <div>
          <div className="tw-flex tw-items-center tw-py-2 tw-px-2 tw-justify-between tw-bg-gray-50 borderHeaderModalDoc">
            <div>
              <p className="tw-text-[12px] tw-font-medium tw-text-gray-700">{t('detail.matchesSection.paidBy')}</p>
            </div>
            <div className="tw-pe-20">
              <p className="tw-text-[12px] tw-font-medium tw-text-gray-700">
                {t('detail.matchesSection.total', { amount: formatMoneyWithCurrency(totalInMatch || 0) })}
              </p>
            </div>
          </div>
          {
            documentInMatches.length > 0 || transactionInMatches.length > 0 || settleInMatches.length > 0 ?
              <div className="tw-px-2 tw-py-2 tw-overflow-auto tw-h-[27vh]">
                {columnHeaders}
                {
                  documentInMatches.length > 0 &&
                  <div>
                    {documentInMatches.map((doc, index) => (
                      <div key={index} className="tw-py-1">
                        <CustomTableItem
                          content={<DocumentContent data={doc} docId={data?.id} type='inMatch' endType={doc?.documentType} originType={data?.documentType} />}
                        />
                      </div>
                    ))}
                  </div>
                }
                {
                  settleInMatches.length > 0 &&
                  <div>
                    {settleInMatches.map((doc, index) => (
                      <div key={index} className="tw-py-1">
                        <CustomTableItem
                          content={<DocumentContent data={doc} docId={data?.id} type='inMatch' endType={doc?.type} originType={data?.documentType} origin={doc?.origin} />}
                        />
                      </div>
                    ))}
                  </div>
                }
                {
                  transactionInMatches.length > 0 &&
                  <div>
                    {transactionInMatches.map((doc, index) => (
                      <div key={index} className="tw-py-1">
                        <CustomTableItem
                          content={<TransactionContent data={doc} docId={data?.id} />}
                        />
                      </div>
                    ))}
                  </div>
                }
              </div>
              :
              <div className="tw-h-[36px]">
                <div className="tw-px-[12px] tw-py-[9px] tw-flex tw-justify-between">
                  <p className="tw-text-[12px] tw-font-normal tw-text-gray-700">{t('detail.matchesSection.noMatchInfo')}</p>
                </div>
              </div>
          }
        </div>
      </div>

      <div className="tw-border-solid tw-border-[1px] tw-border-gray-200 tw-rounded-[3px] tw-mt-4 tw-flex tw-flex-col tw-gap-4">
        <div>
          <div className="tw-flex tw-items-center tw-py-2 tw-px-2 tw-justify-between tw-bg-gray-50 borderHeaderModalDoc">
            <div>
              <p className="tw-text-[12px] tw-font-medium tw-text-gray-700">{t('detail.matchesSection.paying')}</p>
            </div>
            <div className="tw-pe-20">
              <p className="tw-text-[12px] tw-font-medium tw-text-gray-700">
                {t('detail.matchesSection.total', { amount: formatMoneyWithCurrency(totalOutMatch || 0) })}
              </p>
            </div>
          </div>
          {
            documentOutMatches.length > 0 || transactionOutMatches.length > 0 ?
              <div className="tw-px-2 tw-py-2 tw-overflow-auto tw-h-[27vh]">
                {columnHeaders}
                {
                  documentOutMatches.length > 0 &&
                  <div>
                    {documentOutMatches.map((doc, index) => (
                      <div key={index} className="tw-py-1">
                        <CustomTableItem
                          content={<DocumentContent data={doc} docId={data?.id} type='outMatch' endType={doc?.documentType} originType={data?.documentType} />}
                        />
                      </div>
                    ))}
                  </div>
                }
                {
                  transactionOutMatches.length > 0 &&
                  <div>
                    {transactionOutMatches.map((doc, index) => (
                      <div key={index} className="tw-py-1">
                        <CustomTableItem
                          content={<TransactionContent data={doc} docId={data?.id} />}
                        />
                      </div>
                    ))}
                  </div>
                }
              </div>
              :
              <div className="tw-h-[36px]">
                <div className="tw-px-[12px] tw-py-[9px] tw-flex tw-justify-between">
                  <p className="tw-text-[12px] tw-font-normal tw-text-gray-700">{t('detail.matchesSection.noMatchInfo')}</p>
                </div>
              </div>
          }
        </div>
      </div>
    </div>
  );
};

export default MatchesTab;
